import React, { useState, useContext } from 'react';
import { AuthContext } from '../auth/AuthContext';
import {
  useBitbucketConfig,
  useConfigureWorkspace,
  useUpdateConfig,
  useDisconnectWorkspace,
  useDiscoverWorkspace,
  useSyncWorkspace,
  useSyncStatus,
  useBitbucketDevelopers,
  useMapDeveloper,
  useBitbucketRepositories,
  useMapRepository,
  useRepositoryMappings,
  useDeleteMapping
} from '../api/bitbucketApi';
import { useQuery } from '@tanstack/react-query';
import { getUsers } from '../api/userApi';
import { getProjects } from '../api/projectApi';
import { FiGitBranch, FiUsers, FiDatabase, FiRefreshCw, FiCheck, FiX, FiAlertCircle, FiSettings, FiLink, FiTrash2 } from 'react-icons/fi';

const BitbucketAdmin = () => {
  const { role } = useContext(AuthContext);
  const currentRole = (role || '').toLowerCase();
  const isAdmin = ['superadmin', 'admin'].includes(currentRole);

  const [workspaceSlug, setWorkspaceSlug] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [syncFrequency, setSyncFrequency] = useState('daily');
  const [selectedDeveloper, setSelectedDeveloper] = useState({});
  const [selectedRepo, setSelectedRepo] = useState({});

  const { data: config, isLoading: configLoading } = useBitbucketConfig();
  const { data: syncStatus } = useSyncStatus();
  const { data: developersData } = useBitbucketDevelopers();
  const { data: unmappedDevsData } = useBitbucketDevelopers(false);
  const { data: reposData } = useBitbucketRepositories();
  const { data: mappingsData } = useRepositoryMappings();
  const { data: usersResponse } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await getUsers();
      return response.data;
    }
  });
  const { data: projectsResponse } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await getProjects();
      return response.data;
    }
  });

  const configureWorkspace = useConfigureWorkspace();
  const updateConfig = useUpdateConfig();
  const disconnectWorkspace = useDisconnectWorkspace();
  const discoverWorkspace = useDiscoverWorkspace();
  const syncWorkspace = useSyncWorkspace();
  const mapDeveloper = useMapDeveloper();
  const mapRepository = useMapRepository();
  const deleteMapping = useDeleteMapping();

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <FiAlertCircle size={48} color="#ef4444" />
        <h2 style={{ marginTop: '1rem' }}>Access Denied</h2>
        <p>Only administrators can access this page.</p>
      </div>
    );
  }

  const handleConfigure = async (e) => {
    e.preventDefault();
    try {
      await configureWorkspace.mutateAsync({ workspace_slug: workspaceSlug, access_token: accessToken });
      setWorkspaceSlug('');
      setAccessToken('');
      alert('Workspace configured successfully!');
    } catch (error) {
      alert(`Configuration failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDiscover = async () => {
    try {
      const result = await discoverWorkspace.mutateAsync();
      alert(`Discovery complete!\nDevelopers: ${result.developers_found}\nRepositories: ${result.repos_found}\nAuto-mapped: ${result.auto_mapped}`);
    } catch (error) {
      alert(`Discovery failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleSync = async () => {
    try {
      await syncWorkspace.mutateAsync();
      alert('Sync started in background. This may take a few minutes.');
    } catch (error) {
      alert(`Sync failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect the workspace? This will stop activity tracking.')) {
      return;
    }
    try {
      await disconnectWorkspace.mutateAsync();
      alert('Workspace disconnected successfully.');
    } catch (error) {
      alert(`Disconnect failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleUpdateSyncFrequency = async () => {
    try {
      await updateConfig.mutateAsync({ sync_frequency: syncFrequency });
      alert('Sync frequency updated successfully!');
    } catch (error) {
      alert(`Update failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleMapDeveloper = async (developerId) => {
    const userId = selectedDeveloper[developerId];
    if (!userId) {
      alert('Please select a user to map.');
      return;
    }
    try {
      await mapDeveloper.mutateAsync({ bitbucket_developer_id: developerId, user_id: userId });
      alert('Developer mapped successfully!');
      setSelectedDeveloper({ ...selectedDeveloper, [developerId]: '' });
    } catch (error) {
      alert(`Mapping failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleMapRepository = async (repoId) => {
    const projectId = selectedRepo[repoId];
    if (!projectId) {
      alert('Please select a project to map.');
      return;
    }
    try {
      await mapRepository.mutateAsync({ repo_id: repoId, project_id: projectId });
      alert('Repository mapped successfully!');
      setSelectedRepo({ ...selectedRepo, [repoId]: '' });
    } catch (error) {
      alert(`Mapping failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!window.confirm('Remove this repository mapping?')) return;
    try {
      await deleteMapping.mutateAsync(mappingId);
      alert('Mapping removed successfully!');
    } catch (error) {
      alert(`Delete failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const developers = developersData?.developers || [];
  const unmappedDevelopers = unmappedDevsData?.developers || [];
  const repositories = reposData?.repositories || [];
  const mappings = mappingsData?.mappings || [];
  const users = usersResponse || [];
  const projects = projectsResponse || [];

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <FiGitBranch /> Bitbucket Integration Admin
        </h1>
        <p style={{ color: '#64748b' }}>Configure organization-wide Bitbucket integration for developer activity tracking</p>
      </div>

      {/* Workspace Configuration */}
      <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <FiSettings /> Workspace Configuration
        </h2>

        {config?.configured ? (
          <div>
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#16a34a', fontWeight: 600, marginBottom: '0.5rem' }}>
                <FiCheck /> Connected
              </div>
              <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                <div><strong>Workspace:</strong> {config.workspace_name || config.workspace_slug}</div>
                <div><strong>Configured:</strong> {new Date(config.configured_at).toLocaleString()}</div>
                {config.last_synced_at && <div><strong>Last Sync:</strong> {new Date(config.last_synced_at).toLocaleString()}</div>}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Sync Frequency</label>
                <select
                  value={syncFrequency}
                  onChange={(e) => setSyncFrequency(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                >
                  <option value="daily">Daily</option>
                  <option value="hourly">Hourly</option>
                  <option value="manual">Manual Only</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  onClick={handleUpdateSyncFrequency}
                  disabled={updateConfig.isLoading}
                  style={{ padding: '0.5rem 1rem', background: '#2C8FC7', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Update
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleDiscover}
                disabled={discoverWorkspace.isLoading}
                style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <FiDatabase /> {discoverWorkspace.isLoading ? 'Discovering...' : 'Discover Now'}
              </button>
              <button
                onClick={handleSync}
                disabled={syncWorkspace.isLoading}
                style={{ padding: '0.5rem 1rem', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <FiRefreshCw /> {syncWorkspace.isLoading ? 'Syncing...' : 'Sync Now'}
              </button>
              <button
                onClick={handleDisconnect}
                disabled={disconnectWorkspace.isLoading}
                style={{ padding: '0.5rem 1rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <FiX /> Disconnect
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleConfigure}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Workspace Slug</label>
              <input
                type="text"
                value={workspaceSlug}
                onChange={(e) => setWorkspaceSlug(e.target.value)}
                placeholder="e.g., mycompany"
                required
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Workspace Access Token</label>
              <input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="Paste your Bitbucket workspace access token"
                required
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              />
              <small style={{ color: '#64748b' }}>Create token in Bitbucket workspace settings with required scopes</small>
            </div>
            <button
              type="submit"
              disabled={configureWorkspace.isLoading}
              style={{ padding: '0.5rem 1.5rem', background: '#2C8FC7', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}
            >
              {configureWorkspace.isLoading ? 'Connecting...' : 'Connect Workspace'}
            </button>
          </form>
        )}
      </div>

      {config?.configured && (
        <>
          {/* Developer Mapping */}
          <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <FiUsers /> Developer Mapping
            </h2>

            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f1f5f9', borderRadius: '4px', fontSize: '0.9rem' }}>
              <strong>Total Developers:</strong> {developers.length} | <strong>Mapped:</strong> {developers.filter(d => d.user_id).length} | <strong>Unmapped:</strong> {unmappedDevelopers.length}
            </div>

            {unmappedDevelopers.length > 0 ? (
              <div>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem', color: '#f59e0b' }}>⚠️ Unmapped Developers ({unmappedDevelopers.length})</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Bitbucket Developer</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Email</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Map to User</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmappedDevelopers.map((dev) => (
                        <tr key={dev.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '0.75rem' }}>
                            <div style={{ fontWeight: 500 }}>{dev.display_name}</div>
                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>@{dev.bitbucket_username}</div>
                          </td>
                          <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>{dev.email || 'N/A'}</td>
                          <td style={{ padding: '0.75rem' }}>
                            <select
                              value={selectedDeveloper[dev.id] || ''}
                              onChange={(e) => setSelectedDeveloper({ ...selectedDeveloper, [dev.id]: e.target.value })}
                              style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                            >
                              <option value="">Select User...</option>
                              {users.map((user) => (
                                <option key={user.id} value={user.id}>
                                  {user.full_name} ({user.email})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <button
                              onClick={() => handleMapDeveloper(dev.id)}
                              disabled={mapDeveloper.isLoading}
                              style={{ padding: '0.4rem 0.8rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                            >
                              <FiLink /> Map
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                <FiCheck size={32} color="#10b981" />
                <p style={{ marginTop: '0.5rem' }}>All developers are mapped!</p>
              </div>
            )}
          </div>

          {/* Repository Mapping */}
          <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <FiGitBranch /> Repository Mapping (Optional)
            </h2>

            <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1rem' }}>
              Map repositories to projects for project-specific activity reports. This is optional - all commits are tracked regardless.
            </p>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Repository</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Current Project</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Map to Project</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {repositories.map((repo) => (
                    <tr key={repo.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '0.75rem' }}>
                        <div style={{ fontWeight: 500 }}>{repo.repo_name}</div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{repo.repo_slug}</div>
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>
                        {repo.projectname ? (
                          <span style={{ color: '#10b981', fontWeight: 500 }}>{repo.projectname}</span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>Not mapped</span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {!repo.project_id && (
                          <select
                            value={selectedRepo[repo.id] || ''}
                            onChange={(e) => setSelectedRepo({ ...selectedRepo, [repo.id]: e.target.value })}
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                          >
                            <option value="">Select Project...</option>
                            {projects.map((project) => (
                              <option key={project.projectid} value={project.projectid}>
                                {project.projectname}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {!repo.project_id ? (
                          <button
                            onClick={() => handleMapRepository(repo.id)}
                            disabled={mapRepository.isLoading}
                            style={{ padding: '0.4rem 0.8rem', background: '#2C8FC7', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                          >
                            <FiLink /> Map
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.85rem', color: '#10b981' }}>✓ Mapped</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Active Mappings */}
          {mappings.length > 0 && (
            <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginBottom: '1rem' }}>Active Repository Mappings ({mappings.length})</h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Repository</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Project</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Mapped By</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map((mapping) => (
                      <tr key={mapping.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '0.75rem', fontWeight: 500 }}>{mapping.repo_name}</td>
                        <td style={{ padding: '0.75rem' }}>{mapping.projectname}</td>
                        <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#64748b' }}>
                          {mapping.mapped_by_name || 'System'}
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <button
                            onClick={() => handleDeleteMapping(mapping.id)}
                            disabled={deleteMapping.isLoading}
                            style={{ padding: '0.4rem 0.8rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                          >
                            <FiTrash2 size={14} /> Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BitbucketAdmin;
